// src/components/SignCustomizationOverlay.js
import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';

import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

// --- helper to normalize color_table.json into { nameLower: '#rrggbb' } ---
function normalizeColorTable(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object') return out;

  const toHex = (r, g, b) => {
    const h = (v) =>
      Math.max(0, Math.min(255, Number(v) || 0))
        .toString(16)
        .padStart(2, '0');
    return `#${h(r)}${h(g)}${h(b)}`;
  };

  for (const [name, val] of Object.entries(raw)) {
    const key = String(name).trim().toLowerCase();
    let hex = null;

    if (typeof val === 'string') {
      hex = val;
    } else if (Array.isArray(val) && val.length >= 3) {
      hex = toHex(val[0], val[1], val[2]);
    } else if (val && typeof val === 'object') {
      const r = val.r ?? val.R ?? val.red;
      const g = val.g ?? val.G ?? val.green;
      const b = val.b ?? val.B ?? val.blue;
      if ([r, g, b].every((v) => Number.isFinite(Number(v)))) {
        hex = toHex(r, g, b);
      }
    }

    if (hex) out[key] = hex;
  }
  return out;
}

// Shared color list (same as original ImageProcessor)
const COLOR_NAMES = [
  'Pink',
  'Beige',
  'Red',
  'Deep Red',
  'Brown',
  'Light Brown',
  'Yellow',
  'Orange',
  'Olive Green',
  'Green',
  'Pine Green',
  'Turquoise',
  'Blue',
  'Deep Blue',
  'Light Blue',
  'Purple',
  'White',
  'Black',
  'Grey',
];

// Capitan session keys (session-only)
const CAPITAN_DISABLED_KEY = 'capitanNudgeDisabled_SIGN_CUSTOMER';

// Capitan CAPITAN_STYLES (bottom-left)
const CAPITAN_STYLES = {
  nudgeBtn: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #aaa',
    background: 'rgba(255,255,255,0.92)',
    lineHeight: '18px',
    fontSize: 14,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  capitanWrap: {
    position: 'fixed',
    left: 20,
    bottom: 20,
    zIndex: 100000,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    pointerEvents: 'auto',
  },
  capitanBox: { position: 'relative', display: 'inline-block' },
  capitanClose: {
    position: 'absolute',
    top: 6,
    left: 6,
    width: 24,
    height: 24,
    border: 'none',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    fontWeight: 700,
    lineHeight: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  capitanImg: { display: 'block', height: 200, maxHeight: '18vmax', width: 'auto' },
};


// ───────────────────────────────────────────────────────────────
// MAIN OVERLAY COMPONENT
// ───────────────────────────────────────────────────────────────
export default function SignCustomizationOverlay({
  ownerEmail,
  ownerUsername,
  customerEmail,
  onClose,
}) {
  // Compute a label for the UI and the actual email used for username resolution
  const { sessionLabel, effectiveUsername } = useMemo(() => {
    // ALWAYS prefer the logged-in Wabushinator username (ownerUsername)
    const ownerPart =
      ownerUsername && String(ownerUsername).trim()
        ? String(ownerUsername).trim()
        : ownerEmail && ownerEmail.includes('@')
        ? ownerEmail.split('@')[0]
        : 'owner';

    // Customer username (before @)
    const customerLocal =
      customerEmail && customerEmail.includes('@')
        ? customerEmail.split('@')[0]
        : 'customer';

    // Username for backend = owner:customer
    const composite = `${ownerPart}:${customerLocal}`;

    return {
      sessionLabel: composite, // Display
      effectiveUsername: composite, // Backend username
    };
  }, [ownerUsername, ownerEmail, customerEmail]);

  // Local state to satisfy ImageProcessor props
  const [filename, setFilename] = useState('');
  const [isProcessed, setIsProcessed] = useState(false);
  const [errorState, setErrorState] = useState(false);
  const [processedImage, setProcessedImage] = useState(null);
  const [overlayImage, setOverlayImage] = useState(null);
  const [dummyColorList, setDummyColorList] = useState([]);
  const [resetTrigger, setResetTrigger] = useState(false);

  // You can adjust this if you want to limit customer runs
  const credits = 1;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 99999,
      }}
    >
      <div
        style={{
          position: 'relative',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          width: '96%',
          maxWidth: '1400px',
          height: '96vh',
          boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1vmin 1vw',
            paddingTop: '0vmin',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <div style={{ fontSize: '2.3vh', fontWeight: 700 }}>
            Customize Your Sign
          </div>
        </div>

        {/* Session Info Bar */}
        <div
          style={{
            padding: '0vh 1vw',
            borderBottom: '1px solid #d1d5db',
            backgroundColor: '#ecfdf5',
            fontSize: '1.8vh',
            color: '#374151',
            display: 'flex',
            flexDirection: 'column',
            lineHeight: '1.4',
            position: 'relative', // for the X button
          }}
        >
          <div>
            <b>Session:</b> {sessionLabel}
          </div>

          <div style={{ opacity: 0.8 }}>
            <b>Customer:</b> {customerEmail}
          </div>

          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              right: '1vmin',
              bottom: '-7vmin',
              width: '6vmin',
              height: '6vmin',
              borderRadius: '0.8vmin',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              fontSize: '5vmin',       // slightly larger for visual centering
              fontWeight: 700,
              fontFamily: 'monospace', // ensures a geometrically centered glyph
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              padding: 0,
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              zIndex: 100,

              // optical correction: moves glyph up a tiny bit
              transform: 'translateY(-2%)',
            }}
            aria-label="Close customization overlay"
          >
            ×
          </button>

        </div>

        {/* Body: embedded ImageProcessor clone */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            backgroundColor: '#f3f4f6',
            padding: '0',
          }}
        >
          <EmbeddedImageProcessor
            // Core pipeline state
            filename={filename}
            setFilename={setFilename}
            isProcessed={isProcessed}
            setIsProcessed={setIsProcessed}
            // IMPORTANT: use the composite owner:customer for backend username
            email={effectiveUsername}
            setErrorState={setErrorState}
            errorState={errorState}
            credits={credits}
            onClose={onClose} 
            processedImage={processedImage}
            setProcessedImage={setProcessedImage}
            overlayImage={overlayImage}
            setOverlayImage={setOverlayImage}
            setColorListExternal={setDummyColorList}
            resetTrigger={resetTrigger}
            onResetComplete={() => setResetTrigger(false)}
            // Disable auto-hydrate (customer starts fresh)
            inputImageUrl={null}
            initialBaseColor={null}
            initialColorList={[]}
            initAdvancedPanelKey={0}
            onStartNewProject={() => setResetTrigger(false)}
            hydrateGate={false}
            projectRoot={null}
            initialRemoveBkgrnd={false}
            initialCloseBkrndHoles={false}
            initialRemoveIslands={false}
            initialSpeckClean={false}
            initialMkFreestanding={false}
            initialBckgrndInflate={40}
            customerEmail={customerEmail}  
            ownerUsername={ownerUsername}
          />
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// EMBEDDED IMAGE PROCESSOR (full clone, slightly renamed)
// ───────────────────────────────────────────────────────────────
function EmbeddedImageProcessor({
  filename,
  setFilename,
  isProcessed,
  setIsProcessed,
  email,
  setErrorState,
  errorState,
  credits,
  processedImage,
  setProcessedImage,
  overlayImage,
  setOverlayImage,
  setColorListExternal,
  resetTrigger,
  onResetComplete,
  inputImageUrl,
  initialBaseColor,
  initialColorList,
  initAdvancedPanelKey,
  onStartNewProject,
  hydrateGate = true,
  projectRoot,
  initialRemoveBkgrnd = false,
  initialCloseBkrndHoles = false,
  initialRemoveIslands = false,
  initialSpeckClean = false,
  initialMkFreestanding = false,
  initialBckgrndInflate = 40,
  onClose,
  customerEmail, 
  ownerUsername,
}) {
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);

  const [inputImage, setInputImage] = useState(null);
  const [numColors, setNumColors] = useState(4);
  const [pendingNumColors, setPendingNumColors] = useState(4);
  const [backgroundInflate, setBackgroundInflate] = useState(50);
  const [inflationValues, setInflationValues] = useState([]);

  const [colorList, setColorList] = useState([]);
  const [dropdownDefaultsVisible, setDropdownDefaultsVisible] = useState(true);
  const [baseColor, setBaseColor] = useState('');
  const [processDisabled, setProcessDisabled] = useState(true);

  const [speckleFlags, setSpeckleFlags] = useState([]);
  const [removeNoisyBackground, setRemoveNoisyBackground] = useState(false);
  const [closeBackgroundHoles, setCloseBackgroundHoles] = useState(true);
  const [thresholdValues, setThresholdValues] = useState([]);
  const [showColorControls, setShowColorControls] = useState(false);
  const [removeSmallIslands, setRemoveSmallIslands] = useState(false);
  const [speckleAutoCleanup, setSpeckleAutoCleanup] = useState(true);
  const [makeFreestanding, setMakeFreestanding] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [processedLoaded, setProcessedLoaded] = useState(false);
  const [overlayLoaded, setOverlayLoaded] = useState(false);

  const [showInputOverlayPanel, setShowInputOverlayPanel] = useState(false);
  const [showAdvancedOverlayPanel, setShowAdvancedOverlayPanel] =
    useState(false);

// ───────────────────────────────
// Capitan overlay state + logic
// ───────────────────────────────
const [capitanDisabled, setCapitanDisabled] = useState(() => {
  return sessionStorage.getItem(CAPITAN_DISABLED_KEY) === '1';
});
const [showCapitan, setShowCapitan] = useState(false);
const [capitanImgSrc, setCapitanImgSrc] = useState('/capitan.svg');
const [capitanActionSvgSrc, setCapitanActionSvgSrc] = useState('/upload.svg');

// Responsive SVG height: Desktop => 24vmin, Mobile => 18vmax
const [capitanSvgHeight, setCapitanSvgHeight] = useState('24vmin');
useEffect(() => {
  const compute = () => {
    const w = window.innerWidth || 0;
    const h = window.innerHeight || 0;
    const isMobilePortrait = w < h;
    setCapitanSvgHeight(isMobilePortrait ? '18vmax' : '24vmin');
  };
  compute();
  window.addEventListener('resize', compute);
  window.addEventListener('orientationchange', compute);
  return () => {
    window.removeEventListener('resize', compute);
    window.removeEventListener('orientationchange', compute);
  };
}, []);

// Capitan dismissal rules
const capitanDismissRef = useRef({ svg: null });

// Unified API (mirrors App.js naming so logic stays consistent)
const capitanApi = useMemo(() => {
  const setPreprocess = () => setCapitanActionSvgSrc('/preprocess.svg');
  return {
    onUploadStart: setPreprocess,
    onProcessStart: () => setCapitanActionSvgSrc('/process.svg'),
    onOpenAdvanced: () => setCapitanActionSvgSrc('/postprocess.svg'),
    // onGoToExtrusions removed: () => setCapitanActionSvgSrc('/upload.svg'),
    onStartNewProject: () => setCapitanActionSvgSrc('/upload.svg'),

    onImageUploadedToInputBox: () => setPreprocess(),
    onUploadImageClick: () => setPreprocess(),
    onUploadClick: () => setPreprocess(),
  };
}, []);

// Show Capitan on overlay mount unless disabled
useEffect(() => {
  if (capitanDisabled) return;
  setCapitanImgSrc('/capitan.svg'); // ✅ animated SVG
  setShowCapitan(true);
}, [capitanDisabled]);

// Re-show Capitan if dismissed and the action SVG changes
useEffect(() => {
  if (capitanDisabled) return;

  const wasDismissed = capitanDismissRef.current.svg !== null;
  const svgChanged =
    wasDismissed && capitanDismissRef.current.svg !== capitanActionSvgSrc;

  if (svgChanged) {
    capitanDismissRef.current = { svg: null };
    setCapitanImgSrc('/capitan.svg'); // ✅ animated SVG
    setShowCapitan(true);
  }
}, [capitanActionSvgSrc, capitanDisabled]);


const dismissCapitan = useCallback(() => {
  capitanDismissRef.current = { svg: capitanActionSvgSrc };
  setShowCapitan(false);
}, [capitanActionSvgSrc]);

const disableCapitan = useCallback(() => {
  sessionStorage.setItem(CAPITAN_DISABLED_KEY, '1');
  setCapitanDisabled(true);
  setShowCapitan(false);
}, []);


  // three.js viewer refs (sign 3D preview – threeContainerRef)
  const threeContainerRef = useRef(null);
  const threeRendererRef = useRef(null);
  const threeSceneRef = useRef(null);
  const threeCameraRef = useRef(null);
  const threeControlsRef = useRef(null);
  const threeAnimIdRef = useRef(null);

  // separate STL viewer refs (viewerContainerRef)
  const viewerContainerRef = useRef(null);
  const viewerRendererRef = useRef(null);
  const viewerSceneRef = useRef(null);
  const viewerCameraRef = useRef(null);
  const viewerControlsRef = useRef(null);
  const viewerAnimationFrameRef = useRef(null);

  const [hasRequested3D, setHasRequested3D] = useState(false);
  const [isRendering3D, setIsRendering3D] = useState(false);
  const [stlUrl, setStlUrl] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);


  const [showSendOverlay, setShowSendOverlay] = useState(false);
  const [sendNotes, setSendNotes] = useState('');
  const canSendToMaker = hasRequested3D && !!stlUrl && !isRendering3D;

  const [showConfirmationOverlay, setShowConfirmationOverlay] = useState(false);

  const orangeBoxRef = useRef(null);

  // --- One-shot auto-open flags (no auto-close) ------------------------------
  const autoOpenBasicOnceRef = useRef(false);
  const autoOpenAdvancedOnceRef = useRef(false);

  const hasSavedThumbnailRef = useRef(false);

  // color table + metadata-derived colors for EMSparts
  const [colorMap, setColorMap] = useState({});
  const [effectiveColors, setEffectiveColors] = useState([]); // ['Base', 'Red', ...]
  const [emsRoot, setEmsRoot] = useState(null);               // EMSparts root for colored 3D

  const nameToHex = useCallback(
    (name) => {
      if (!name) return null;
      const key = String(name).trim().toLowerCase();
      return colorMap[key] || null;
    },
    [colorMap]
  );

  const colorsForUi = useMemo(
    () => (Array.isArray(effectiveColors) ? effectiveColors : []),
    [effectiveColors]
  );

  const emsPartUrls = useMemo(() => {
    if (!emsRoot || !Array.isArray(colorsForUi) || colorsForUi.length === 0)
      return [];
    return colorsForUi.map((c, idx) => {
      const index = idx + 1; // 1 - ColorName.stl, 2 - ...
      return `${emsRoot}/stls/EMSparts/${index} - ${c}.stl`;
    });
  }, [emsRoot, colorsForUi]);


  const emsPartColors = useMemo(() => {
    if (!Array.isArray(colorsForUi)) return [];
    return colorsForUi.map((c) => nameToHex(c) || null);
  }, [colorsForUi, nameToHex]);

  // hydrate effectiveColors from metadata (when projectRoot is known)
  useEffect(() => {
    if (!projectRoot) return;

    (async () => {
      try {
        const res = await fetch(
          `${projectRoot}/metadata.json?t=${Date.now()}`,
          { cache: 'no-store' }
        );
        if (!res.ok) return;
        const meta = await res.json();

        const normPath = (p) =>
          String(p || '')
            .replace(/^\.\//, '')
            .replace(/\/+/g, '/')
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        const baseSvgRel = String(meta?.base_svg || '')
          .replace(/^\.\//, '')
          .trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== 'base')
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) =>
                e.file ? pickColorName(e.file) : String(e.name || '')
              )
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find(
            (c) => String(c?.name).toLowerCase() === 'base'
          );
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const parsed = [base, ...rest].filter(Boolean);
        if (parsed.length >= 1) {
          setEffectiveColors(parsed);
        }
      } catch (e) {
        console.warn('[SignOverlay] metadata color hydrate failed', e);
      }
    })();
  }, [projectRoot]);

  // fetch color_table.json → colorMap
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/color_table.json', { cache: 'no-cache' });
        if (!res.ok) throw new Error('Missing /color_table.json');
        const data = await res.json();
        if (!cancelled) setColorMap(normalizeColorTable(data));
      } catch {
        try {
          const res2 = await fetch('/color-table/', { cache: 'no-cache' });
          if (res2.ok && !cancelled) {
            const data2 = await res2.json();
            setColorMap(normalizeColorTable(data2));
          }
        } catch {
          // silently ignore
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- three.js 3D preview setup (Sign – threeContainerRef) ---
  useEffect(() => {
    if (!hasRequested3D) return;
    const container = threeContainerRef.current;
    if (!container) return;

    // If already initialized, just update size
    if (threeRendererRef.current && threeCameraRef.current) {
      const width = container.clientWidth || 400;
      const height = container.clientHeight || 400;
      threeRendererRef.current.setSize(width, height);
      threeCameraRef.current.aspect = width / height;
      threeCameraRef.current.updateProjectionMatrix();
      return;
    }

    const width = container.clientWidth || 400;
    const height = container.clientHeight || 400;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 1000);
    camera.position.set(0, 0, 150);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio || 1);

    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.rotateSpeed = 0.8;
    controls.zoomSpeed = 0.8;

    const ambient = new THREE.AmbientLight(0xffffff, 0.7);
    const key = new THREE.DirectionalLight(0xffffff, 0.8);
    key.position.set(1, 1, 1);
    const fill = new THREE.DirectionalLight(0xffffff, 0.5);
    fill.position.set(-1, 0.5, -1);
    scene.add(ambient, key, fill);

    threeSceneRef.current = scene;
    threeCameraRef.current = camera;
    threeRendererRef.current = renderer;
    threeControlsRef.current = controls;

    const animate = () => {
      threeAnimIdRef.current = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      if (!threeRendererRef.current || !threeCameraRef.current || !container)
        return;
      const w = container.clientWidth || 400;
      const h = container.clientHeight || 400;
      threeRendererRef.current.setSize(w, h);
      threeCameraRef.current.aspect = w / h;
      threeCameraRef.current.updateProjectionMatrix();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (threeAnimIdRef.current) cancelAnimationFrame(threeAnimIdRef.current);
      if (threeRendererRef.current) {
        threeRendererRef.current.dispose();
        threeRendererRef.current = null;
      }
      threeSceneRef.current = null;
      threeCameraRef.current = null;
      threeControlsRef.current = null;
      if (container) container.innerHTML = '';
    };
  }, [hasRequested3D]);

  const captureAndUploadThumbnail = useCallback(() => {
    const renderer = threeRendererRef.current;
    const scene = threeSceneRef.current;
    const camera = threeCameraRef.current;

    if (!renderer || !scene || !camera || hasSavedThumbnailRef.current) return;

    const canvas = renderer.domElement;
    if (!canvas) return;

    // mark as done so we don't spam the backend
    hasSavedThumbnailRef.current = true;

    // --- store original renderer/camera state ---
    const oldBackground = scene.background;
    const oldPixelRatio = renderer.getPixelRatio();
    const oldSize = new THREE.Vector2();
    renderer.getSize(oldSize);
    const oldAspect = camera.aspect;
    const oldPosition = camera.position.clone();

    try {
      // --- compute bounding box of the whole scene ---
      const box = new THREE.Box3().setFromObject(scene);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const maxSize = Math.max(size.x, size.y, size.z) || 1;

      // distance so object fits into view for current FOV
      const fov = (camera.fov * Math.PI) / 180;
      let distance = maxSize / (2 * Math.tan(fov / 2));
      distance *= 1.2; // margin so model isn't cropped

      // move camera along its current view direction so it still looks at center
      const dir = new THREE.Vector3();
      camera.getWorldDirection(dir);
      dir.normalize();

      const newPos = center.clone().sub(dir.multiplyScalar(distance));
      camera.position.copy(newPos);
      camera.lookAt(center);
      camera.updateMatrixWorld();

      // --- prep transparent 1200x1200 render for thumbnail ---
      scene.background = null;             // no background color
      renderer.setClearColor(0x000000, 0); // transparent clear
      renderer.setPixelRatio(1);           // avoid ultra-huge canvases
      renderer.setSize(2000, 2000, false); // thumbnail resolution

      camera.aspect = 1;                   // square aspect
      camera.updateProjectionMatrix();

      // render a single frame in this configuration
      renderer.render(scene, camera);
    } catch (e) {
      console.error(
        '[SignCustomizationOverlay] Error preparing thumbnail render:',
        e
      );
    }

    canvas.toBlob(
      async (blob) => {
        // --- restore original renderer/camera state as soon as we have the blob ---
        try {
          const usernameForPath =
            (email || '').split('@')[0] || email || 'guest';

          const baseNoExt = String(filename || '')
            .split('.')
            .slice(0, -1)
            .join('');

          if (!usernameForPath || !baseNoExt) {
            console.error(
              '[SignCustomizationOverlay] Missing username or project for thumbnail upload'
            );
            return;
          }

          const formData = new FormData();
          // must be "file" to match UploadFile = File(...)
          formData.append('file', blob, 'thumbnail.png');
          // username: Form(...)
          formData.append('username', usernameForPath);
          // project: Form(...)  (no "output-" prefix)
          formData.append('project', baseNoExt);

          const res = await fetch('/upload-thumbnail/', {
            method: 'POST',
            body: formData,
          });

          let json = null;
          try {
            json = await res.json();
          } catch (parseErr) {
            console.warn(
              '[SignCustomizationOverlay] Could not parse /upload-thumbnail/ JSON response',
              parseErr
            );
          }

          console.log(
            '[SignCustomizationOverlay] /upload-thumbnail/ status:',
            res.status,
            'response:',
            json
          );

          if (!res.ok || (json && json.status === 'error')) {
            console.error(
              '[SignCustomizationOverlay] Thumbnail upload failed with response:',
              json
            );
            // allow retry if backend rejected
            hasSavedThumbnailRef.current = false;
          } else {
            console.log(
              '[SignCustomizationOverlay] ✅ Thumbnail upload succeeded'
            );

            // After a successful upload, point the UI to the new thumbnail
            const publicRoot = `/preview/${usernameForPath}/output-${baseNoExt}`;
            const thumbUrl = `${normalizeUrl(
              `${publicRoot}/thumbnail.png`
            )}?t=${Date.now()}`;
            setThumbnailUrl(thumbUrl);
          }
        } catch (err) {
          console.error(
            '[SignCustomizationOverlay] thumbnail upload failed:',
            err
          );
          // optional: allow retry on next render
          hasSavedThumbnailRef.current = false;
        }

      },
      'image/png'
    );
  }, [email, filename]);


  // Load STL and center it in the view whenever stlUrl changes (three* refs)
  useEffect(() => {
    if (!stlUrl || !threeSceneRef.current || !threeCameraRef.current) return;

    const scene = threeSceneRef.current;

    // Remove any previous sign meshes/groups
    const oldSingle = scene.getObjectByName('signMesh');
    if (oldSingle) scene.remove(oldSingle);
    const oldGroup = scene.getObjectByName('signMeshGroup');
    if (oldGroup) scene.remove(oldGroup);

    const loader = new STLLoader();

    // If we have EMSparts and colors, load them individually and color like STLGenerator
    if (emsPartUrls && emsPartUrls.length > 0) {
      const group = new THREE.Group();
      group.name = 'signMeshGroup';
      scene.add(group);

      let remaining = emsPartUrls.length;

      emsPartUrls.forEach((url, idx) => {
        loader.load(
          url,
          (geometry) => {
            try {
              const colorHex = emsPartColors[idx] || '#dddddd';
              const material = new THREE.MeshStandardMaterial({
                color: new THREE.Color(colorHex),
                metalness: 0.2,
                roughness: 0.6,
              });

              const mesh = new THREE.Mesh(geometry, material);
              group.add(mesh);
            } catch (err) {
              console.error(
                '[SignCustomizationOverlay] EMSpart load error:',
                url,
                err
              );
            } finally {
              remaining -= 1;

              // When all parts loaded, compute group bounding box once
              if (remaining === 0 && group.children.length > 0) {
                const box = new THREE.Box3().setFromObject(group);
                const sphere = box.getBoundingSphere(new THREE.Sphere());
                const center = sphere.center.clone();

                // Center group at origin
                group.position.sub(center);

                const radius = sphere.radius || 1;
                const camera = threeCameraRef.current;
                if (camera) {
                  const fitOffset = 1.2;
                  const fov = THREE.MathUtils.degToRad(camera.fov);
                  const dist = (radius / Math.sin(fov / 2)) * fitOffset;

                  // *** angled view ***
                  const tiltDeg = 25; // adjust for more/less angle
                  const tilt = THREE.MathUtils.degToRad(tiltDeg);

                  camera.position.set(
                    dist * Math.sin(tilt),
                    dist * Math.sin(tilt),
                    dist * Math.cos(tilt)
                  );

                  camera.near = Math.max(0.1, radius / 100);
                  camera.far = Math.max(5000, radius * 20);
                  camera.lookAt(0, 0, 0);
                  camera.updateProjectionMatrix();

                  if (threeControlsRef.current) {
                    threeControlsRef.current.target.set(0, 0, 0);
                    threeControlsRef.current.update();
                  }
                }

                // NEW: wait one frame so the new camera/mesh are rendered, then capture
                requestAnimationFrame(() => {
                  captureAndUploadThumbnail();
                });
              }
            }
          },
          undefined,
          (err) => {
            console.error(
              '[SignCustomizationOverlay] EMSpart STL load error:',
              url,
              err
            );
            remaining -= 1;
          }
        );
      });

      return; // don't also load the combined STL
    }

    // Fallback: single combined STL (neutral color)
    loader.load(
      stlUrl,
      (geometry) => {
        try {
          geometry.computeBoundingBox();
          const bbox = geometry.boundingBox;
          const center = bbox.getCenter(new THREE.Vector3());
          geometry.translate(-center.x, -center.y, -center.z); // center at origin

          const material = new THREE.MeshStandardMaterial({
            color: 0xdddddd,
            metalness: 0.2,
            roughness: 0.7,
          });

          const mesh = new THREE.Mesh(geometry, material);
          mesh.name = 'signMesh';

          const size = bbox.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z) || 1;
          const radius = maxDim / 2;

          const camera = threeCameraRef.current;
          if (camera) {
            const fitOffset = 1.2;
            const fov = THREE.MathUtils.degToRad(camera.fov);
            const dist = (radius / Math.sin(fov / 2)) * fitOffset;

            // *** angled view ***
            const tiltDeg = 25; // adjust as desired
            const tilt = THREE.MathUtils.degToRad(tiltDeg);

            camera.position.set(
              dist * Math.sin(tilt),
              dist * Math.sin(tilt),
              dist * Math.cos(tilt)
            );

            camera.near = Math.max(0.1, radius / 100);
            camera.far = Math.max(5000, radius * 20);
            camera.lookAt(0, 0, 0);
            camera.updateProjectionMatrix();

            if (threeControlsRef.current) {
              threeControlsRef.current.target.set(0, 0, 0);
              threeControlsRef.current.update();
            }
          }

          scene.add(mesh);
          
          // NEW: wait one frame so the new camera/mesh are rendered, then capture
          requestAnimationFrame(() => {
            captureAndUploadThumbnail();
          });
        } catch (err) {
          console.error(
            '[SignCustomizationOverlay] STL centering failed:',
            err
          );
        }
      },
      undefined,
      (err) => {
        console.error('[SignCustomizationOverlay] STL load error:', err);
      }
    );
  }, [stlUrl, emsPartUrls, emsPartColors, captureAndUploadThumbnail]);



  // --- second STL viewer (viewerContainerRef) – kept intact but separate ---
  useEffect(() => {
    if (!hasRequested3D || !stlUrl) return;
    const container = viewerContainerRef.current;
    if (!container) return;

    // Clean up any previous viewer
    if (viewerRendererRef.current) {
      try {
        container.removeChild(viewerRendererRef.current.domElement);
      } catch (e) {
        // ignore
      }
      viewerRendererRef.current.dispose();
      viewerRendererRef.current = null;
    }
    if (viewerAnimationFrameRef.current) {
      cancelAnimationFrame(viewerAnimationFrameRef.current);
      viewerAnimationFrameRef.current = null;
    }

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8fafc);

    const width = container.clientWidth || container.offsetWidth || 300;
    const height = container.clientHeight || container.offsetHeight || 300;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.set(0, 0, 150);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    const hemi = new THREE.HemisphereLight(0xffffff, 0x444444, 1.0);
    hemi.position.set(0, 200, 0);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 0.8);
    dir.position.set(0, 0, 200);
    scene.add(dir);

    viewerSceneRef.current = scene;
    viewerCameraRef.current = camera;
    viewerRendererRef.current = renderer;
    viewerControlsRef.current = controls;

    setIsRendering3D(true);

    const loader = new STLLoader();
    loader.load(
      stlUrl,
      (geometry) => {
        const material = new THREE.MeshStandardMaterial({
          color: 0x444444,
          metalness: 0.2,
          roughness: 0.6,
        });

        const mesh = new THREE.Mesh(geometry, material);
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        mesh.position.sub(center);

        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        const targetSize = 80;
        const scale = targetSize / maxDim;
        mesh.scale.set(scale, scale, scale);

        scene.add(mesh);

        camera.position.set(0, 0, maxDim * 2);
        camera.lookAt(0, 0, 0);

        const animate = () => {
          viewerAnimationFrameRef.current = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();

        setIsRendering3D(false);
      },
      undefined,
      (err) => {
        console.error('[SignCustomizationOverlay] STL load error:', err);
        setIsRendering3D(false);
      }
    );

    const handleResize = () => {
      if (!viewerRendererRef.current || !viewerCameraRef.current) return;
      const w = container.clientWidth || container.offsetWidth || 300;
      const h = container.clientHeight || container.offsetHeight || 300;
      viewerRendererRef.current.setSize(w, h);
      viewerCameraRef.current.aspect = w / h;
      viewerCameraRef.current.updateProjectionMatrix();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (viewerAnimationFrameRef.current) {
        cancelAnimationFrame(viewerAnimationFrameRef.current);
        viewerAnimationFrameRef.current = null;
      }
      if (viewerRendererRef.current) {
        viewerRendererRef.current.dispose();
        try {
          if (viewerRendererRef.current.domElement.parentNode === container) {
            container.removeChild(viewerRendererRef.current.domElement);
          }
        } catch (e) {
          // ignore
        }
        viewerRendererRef.current = null;
      }
      viewerSceneRef.current = null;
      viewerCameraRef.current = null;
      viewerControlsRef.current = null;
    };
  }, [hasRequested3D, stlUrl]);

  // --- URL helpers ------------------------------------------------------------
  const toPreviewPrefix = (url) => {
    if (!url) return '';
    const s = String(url).trim();
    if (s.startsWith('/preview/')) return s;
    if (s.startsWith('/outputs/'))
      return '/preview/' + s.slice('/outputs/'.length);
    return s;
  };

  const normalizeUrl = (p) => {
    if (!p) return '';
    let url = String(p).trim();

    if (
      /^(https?:)?\/\//i.test(url) ||
      /^data:/i.test(url) ||
      url.startsWith('blob:')
    ) {
      try {
        const u = new URL(url, window.location.origin);
        u.pathname = toPreviewPrefix(u.pathname);
        return u.toString();
      } catch {
        return toPreviewPrefix(url);
      }
    }

    if (!url.startsWith('/')) url = '/' + url;
    url = toPreviewPrefix(url);
    return url;
  };

  const extractProjectRoot = (urlLike) => {
    if (!urlLike) return '';
    const s = String(urlLike);
    the: {
      const m = s.match(/(\/(?:outputs|preview)\/[^/]+\/output-[^/]+)/i);
      return m ? m[1] : '';
    }
  };

  const buildRootFromEmailFilename = (emailArg, filenameArg) => {
    const username = (emailArg || '').split('@')[0];
    const baseNoExt = String(filenameArg || '')
      .split('.')
      .slice(0, -1)
      .join('');
    if (!username || !baseNoExt) return '';
    return `/preview/${username}/output-${baseNoExt}`;
  };

  const userIntentRef = useRef('idle'); // 'idle' | 'upload' | 'open'

  const paddedInflate = [
    backgroundInflate,
    ...Array(pendingNumColors - 1)
      .fill(0)
      .map((_, i) => Number(inflationValues[i + 1] ?? 0)),
  ];
  const paddedSpeckles = [...Array(pendingNumColors)].map((_, i) =>
    Number(speckleFlags[i] ?? 0)
  );
  const paddedThresholds = [...Array(pendingNumColors)].map((_, i) =>
    Number(thresholdValues[i] ?? 0)
  );

  const colorUpdateTimer = useRef(null);
  const lastSentColorsRef = useRef(null);
  const canSendColorUpdates = () =>
    isProcessed && !dropdownDefaultsVisible && !isProcessing;

  const scheduleColorUpdate = (nextSelectedColors) => {
    if (!canSendColorUpdates()) return;
    const key = JSON.stringify(nextSelectedColors);
    if (lastSentColorsRef.current === key) return;
    if (colorUpdateTimer.current) clearTimeout(colorUpdateTimer.current);
    colorUpdateTimer.current = setTimeout(() => {
      lastSentColorsRef.current = key;
      triggerColorUpdate(nextSelectedColors);
    }, 350);
  };

  const triggerColorUpdate = async (selectedColors) => {
    try {
      setIsProcessing(true);
      const res = await fetch('/update-colors/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: selectedColors.length,
          selectedColors,
        }),
      });
      if (!res.ok) {
        console.error('update-colors failed:', await res.text());
        setErrorState(true);
        setIsProcessing(false);
        return;
      }
      const data = await res.json().catch(() => ({}));
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      updatePreviews(data);
      setErrorState(false);
    } catch (e) {
      console.error('update-colors error:', e);
      setErrorState(true);
      setIsProcessing(false);
    }
  };

  // ----------------- HARD RESET before new upload -----------------
  function resetAllLocalStateForNewUpload() {
    setMakeFreestanding(false);
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }
    lastSentColorsRef.current = null;

    setInputImage(null);
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessed(false);
    setProcessDisabled(false);
    setErrorState(false);

    setBaseColor('');
    setColorList([]);
    setNumColors(4);
    setPendingNumColors(4);
    setDropdownDefaultsVisible(true);
    setShowColorControls(false);
    setSpeckleFlags([0, 0, 0, 0, 0, 0, 0, 0]);
    setThresholdValues(Array(8).fill(0));
    setInflationValues([]);
    setBackgroundInflate(50);
    setRemoveNoisyBackground(false);
    setCloseBackgroundHoles(true);
    setRemoveSmallIslands(false);
    setSpeckleAutoCleanup(true);
    setMakeFreestanding(false);
    setProgress(null);

    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    setShowInputOverlayPanel(false);
    setShowAdvancedOverlayPanel(false);

    // Reset 3D preview state
    setHasRequested3D(false);
    setIsRendering3D(false);
    setStlUrl(null);
    setThumbnailUrl(null);

    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  // scroll orangeBox when 3D requested
  useEffect(() => {
    if (hasRequested3D && orangeBoxRef.current) {
      orangeBoxRef.current.scrollTo({
        top: orangeBoxRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [hasRequested3D]);

  // ----------------- OPEN PROJECT hydrate -----------------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    userIntentRef.current = 'open';
    setShowColorControls(true);

    if (!inputImageUrl && !filename && !initAdvancedPanelKey) return;

    if (inputImageUrl) setInputImage(inputImageUrl);
    setProcessDisabled(false);
    setIsProcessed(true);

    const derivedRoot =
      projectRoot ||
      extractProjectRoot(inputImageUrl || '') ||
      buildRootFromEmailFilename(email, filename);

    if (!derivedRoot) {
      console.warn(
        '[ImageProcessor] Could not determine project root. Pass projectRoot from App.js.'
      );
    }

    const ts = Date.now();
    if (derivedRoot) {
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(`${derivedRoot}/vectors/combined_colors.png`)}?t=${ts}`
      );
    }
    setDropdownDefaultsVisible(false);

    (async () => {
      if (!derivedRoot) return;
      const metaUrl = `${normalizeUrl(
        `${derivedRoot}/metadata.json`
      )}?ts=${Date.now()}`;
      try {
        const res = await fetch(metaUrl, { cache: 'no-store' });
        if (!res.ok) {
          console.warn(
            '[ImageProcessor] metadata fetch failed:',
            res.status,
            res.statusText
          );
          return;
        }
        const meta = await res.json();

        let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
        if (baseRel) {
          if (!baseRel.includes('/')) baseRel = `vectors/base/${baseRel}`;
          setProcessedLoaded(false);
          setProcessedImage(
            `${normalizeUrl(`${derivedRoot}/${baseRel}`)}?t=${Date.now()}`
          );
        } else {
          console.warn(
            '[ImageProcessor] metadata.base_svg missing; overlay will be visible alone.'
          );
        }

        const normPath = (p) =>
          String(p || '')
            .replace(/^\.\//, '')
            .replace(/\/+/g, '/')
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        const snap = (c) => {
          if (!c) return '';
          const found = COLOR_NAMES.find(
            (n) => n.toLowerCase() === String(c).toLowerCase()
          );
          return found || c;
        };

        const baseSvgRel = String(meta?.base_svg || '')
          .replace(/^\.\//, '')
          .trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== 'base')
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) =>
                e.file ? pickColorName(e.file) : String(e.name || '')
              )
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find(
            (c) => String(c?.name).toLowerCase() === 'base'
          );
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const baseNorm = snap(base);
        const restNorm = rest.map(snap).filter(Boolean);
        const parsed = [baseNorm, ...restNorm].filter(Boolean);

        if (parsed.length >= 2) {
          const total = Math.min(8, Math.max(2, parsed.length));
          setBaseColor(parsed[0]);
          setColorList(parsed.slice(1));
          setNumColors(total);
          setPendingNumColors(total);
          setDropdownDefaultsVisible(false);
          setIsProcessed(true);
        }
      } catch (err) {
        console.error('[ImageProcessor] metadata load error:', err);
        requestAnimationFrame(() => setDropdownDefaultsVisible(false));
      }
    })();
  }, [inputImageUrl, initAdvancedPanelKey, filename, projectRoot]); // eslint-disable-line

  // ---------- Hydrate processing settings ----------
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;

    const toBool = (v, fb = false) => {
      if (typeof v === 'boolean') return v;
      if (typeof v === 'number') return v !== 0;
      if (typeof v === 'string') {
        const t = v.trim().toLowerCase();
        if (['true', '1', 'yes', 'y', 'on'].includes(t)) return true;
        if (['false', '0', 'no', 'n', 'off'].includes(t)) return false;
      }
      return fb;
    };
    const clampInt = (n, lo, hi, fb) => {
      const v = parseInt(n, 10);
      if (!Number.isFinite(v)) return fb;
      return Math.max(lo, Math.min(hi, v));
    };

    setRemoveNoisyBackground(toBool(initialRemoveBkgrnd, false));
    setCloseBackgroundHoles(toBool(initialCloseBkrndHoles, false));
    setRemoveSmallIslands(toBool(initialRemoveIslands, false));
    setSpeckleAutoCleanup(toBool(initialSpeckClean, false));
    setMakeFreestanding(toBool(initialMkFreestanding, false));

    const safeInflate = clampInt(initialBckgrndInflate, 10, 100, 40);
    setBackgroundInflate(safeInflate);
  }, [
    hydrateGate,
    initAdvancedPanelKey,
    projectRoot,
    initialRemoveBkgrnd,
    initialCloseBkrndHoles,
    initialRemoveIslands,
    initialSpeckClean,
    initialMkFreestanding,
    initialBckgrndInflate,
  ]);

  // Reset on external reset
  useEffect(() => {
    if (resetTrigger) {
      setInputImage(null);
      setProcessedImage(null);
      setOverlayImage(null);
      setFilename('');
      setIsProcessed(false);
      setErrorState(false);
      setColorListExternal([]);
      setBaseColor('');
      setColorList([]);
      setPendingNumColors(4);
      setNumColors(4);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
      setIsProcessing(false);
      setProcessedLoaded(false);
      setOverlayLoaded(false);
      setShowInputOverlayPanel(false);
      setShowAdvancedOverlayPanel(false);
      onResetComplete?.();
      if (fileInputRef.current) fileInputRef.current.value = '';
      userIntentRef.current = 'idle';
    }
  }, [
    resetTrigger,
    onResetComplete,
    setProcessedImage,
    setOverlayImage,
    setFilename,
    setIsProcessed,
    setErrorState,
    setColorListExternal,
  ]);

  // ---------- Robust preview updater ----------
  const updatePreviews = (data) => {
    const ts = Date.now();
    const d = data || {};

    const preview =
      d.preview_image ||
      d.previewImage ||
      d.processed_image ||
      d.processedImage ||
      d.base_svg ||
      d.baseSvg ||
      null;

    const overlay =
      d.overlay_image ||
      d.overlayImage ||
      d.combined_colors ||
      d.combinedColors ||
      d.flat_image ||
      d.flatImage ||
      null;

    if (preview) {
      setProcessedLoaded(false);
      setProcessedImage(`${normalizeUrl(preview)}?t=${ts}`);
    } else if (processedImage) {
      setProcessedLoaded(false);
      setProcessedImage(
        `${normalizeUrl(processedImage.split('?')[0])}?t=${ts}`
      );
    }

    if (overlay) {
      setOverlayLoaded(false);
      setOverlayImage(`${normalizeUrl(overlay)}?t=${ts}`);
    } else if (overlayImage) {
      setOverlayLoaded(false);
      setOverlayImage(
        `${normalizeUrl(overlayImage.split('?')[0])}?t=${ts}`
      );
    }
  };

  // sync internal -> external color list
  useEffect(() => {
    if (typeof setColorListExternal !== 'function') return;
    if (baseColor && colorList.length > 0) {
      setColorListExternal([baseColor, ...colorList]);
    } else if (baseColor) {
      setColorListExternal([baseColor]);
    } else {
      setColorListExternal(colorList);
    }
  }, [baseColor, colorList, setColorListExternal]);

  const handleInputClick = () => {
    userIntentRef.current = 'upload';

    // Capitan: upload => preprocess
    capitanApi.onUploadStart();

    resetAllLocalStateForNewUpload();
    onStartNewProject?.();
    fileInputRef.current?.click();
  };

  // Size arrays when color count changes
  useEffect(() => {
    setThresholdValues(Array(pendingNumColors).fill(0));
    setSpeckleFlags([0, ...Array(pendingNumColors - 1).fill(0)]);
  }, [pendingNumColors]);

  // Progress polling (uses email -> username-part)
  useEffect(() => {
    const username = (email || '').split('@')[0]; // email may be "owner:customer" (no '@') → username becomes whole string
    if (!username) return;
    const interval = setInterval(() => {
      fetch(`/progress/${username}?ts=${Date.now()}`)
        .then((res) => {
          if (!res.ok) throw new Error('Progress not ready');
          return res.json();
        })
        .then((data) => {
          if (typeof data.progress === 'number') {
            setProgress(data.progress);
            setErrorState(false);
          }
        })
        .catch(() => setProgress(null));
    }, 1500);
    return () => clearInterval(interval);
  }, [email, setErrorState]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Only JPG, JPEG, or PNG files are allowed.');
      return;
    }

    const formData = new FormData();
    const username = (email || '').split('@')[0];
    formData.append('file', file);
    formData.append('username', username);

    setInputImage(URL.createObjectURL(file));
    setIsProcessed(false);
    setDropdownDefaultsVisible(true);
    setBaseColor('');
    setColorList([]);
    setProcessDisabled(false);
    setProcessedImage(null);
    setOverlayImage(null);
    setIsProcessing(false);
    setProcessedLoaded(false);
    setOverlayLoaded(false);

    try {
      const response = await fetch('/upload/', {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setFilename(data.filename);
        setErrorState(false);
      // Auto-open Basic Controls (one-shot, stays open)
      if (!autoOpenBasicOnceRef.current) {
        autoOpenBasicOnceRef.current = true;
        setShowInputOverlayPanel(true);
      }

      } else {
        console.error('Upload failed:', await response.text());
        setErrorState(true);
      }
    } catch (error) {
      console.error('Error during upload:', error);
      setErrorState(true);
    }

    orangeBoxRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleProcessClick = async (collectInputs = false) => {
    capitanApi.onProcessStart();
    if (colorUpdateTimer.current) {
      clearTimeout(colorUpdateTimer.current);
      colorUpdateTimer.current = null;
    }
    lastSentColorsRef.current = null;

    // Any new processing invalidates previous 3D preview
    setHasRequested3D(false);
    setIsRendering3D(false);
    setStlUrl(null);

    if (!collectInputs) {
      setBaseColor('');
      setColorList([]);
      setDropdownDefaultsVisible(true);
      setShowColorControls(false);
    }

    let selectedColors = [];
    let inflateValuesLocal = [];
    if (collectInputs) {
      selectedColors = [
        (baseColor || '').trim(),
        ...Array.from({ length: pendingNumColors - 1 }, (_, i) =>
          (colorList[i] || '').trim()
        ),
      ].slice(0, pendingNumColors);
      inflateValuesLocal = [
        backgroundInflate,
        ...inflationValues.slice(1, pendingNumColors),
      ];
    }

    setNumColors(pendingNumColors);
    if (!collectInputs) setInflationValues(Array(pendingNumColors).fill(0));

    autoOpenAdvancedOnceRef.current = false; // allow auto-open for this new processing run

    setIsProcessing(true);
    setProcessDisabled(true);

    setProcessedImage(null);
    setOverlayImage(null);
    setProcessedLoaded(false);
    setOverlayLoaded(false);
    setIsProcessed(false);

    try {
      const response = await fetch('/process/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: pendingNumColors,
          backgroundInflate,
          inflateValues: collectInputs ? inflateValuesLocal : [],
          selectedColors: collectInputs ? selectedColors : [],
          speckleFlags: collectInputs ? speckleFlags : [],
          thresholdValues: Array.from(
            { length: pendingNumColors },
            (_, i) => thresholdValues[i] || 0
          ),
          removeNoisyBackground,
          closeBackgroundHoles,
          removeSmallIslands,
          speckleAutoCleanup,
          makeFreestanding,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        if (
          data.adjusted_num_colors &&
          data.adjusted_num_colors !== pendingNumColors
        ) {
          setNumColors(data.adjusted_num_colors);
          setPendingNumColors(data.adjusted_num_colors);
        }

        updatePreviews(data);
        setDropdownDefaultsVisible(false);
        setErrorState(false);
        setIsProcessed(true);

        if (Array.isArray(data.color_names) && data.color_names.length > 0) {
          const baseResolved = COLOR_NAMES.find(
            (c) => c.toLowerCase() === data.color_names[0].toLowerCase()
          );
          setBaseColor(baseResolved || data.color_names[0]);
          const userColors = data.color_names
            .slice(1)
            .map(
              (c) =>
                COLOR_NAMES.find(
                  (name) => name.toLowerCase() === c.toLowerCase()
                ) || c
            );
          setColorList(userColors);
        } else {
          setColorList([]);
        }
      } else {
        console.error('Processing failed:', await response.text());
        setErrorState(true);
      }
    } catch (error) {
      console.error('Error during processing:', error);
      setErrorState(true);
    } finally {
      setProcessDisabled(false);
    }

    orangeBoxRef.current?.scrollTo({
      top: orangeBoxRef.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  // ---- Reusable Update Design ----
  const handleUpdateDesign = async (overrides = {}) => {
    const {
      num = numColors,
      base = baseColor,
      colors = colorList,
      background = backgroundInflate,
      inflate = paddedInflate,
      speckles = paddedSpeckles,
      thresholds = paddedThresholds,
      rnb = removeNoisyBackground,
      cbh = closeBackgroundHoles,
      rsi = removeSmallIslands,
      sac = speckleAutoCleanup,
      mfs = makeFreestanding,
    } = overrides;

    setIsProcessing(true);
    window.dispatchEvent(new CustomEvent('resetSTLDownloads'));

    setProcessedLoaded(false);
    setOverlayLoaded(false);

    try {
      const selectedColorsLocal = [
        (base || '').trim(),
        ...Array.from(
          { length: Math.max(0, (num || 0) - 1) },
          (_, i) => (colors[i] || '').trim()
        ),
      ].slice(0, num || 0);

      const response = await fetch('/update-design/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename,
          email,
          numColors: num,
          backgroundInflate: background,
          inflateValues: inflate,
          selectedColors: selectedColorsLocal,
          speckleFlags: speckles,
          thresholdValues: thresholds,
          removeNoisyBackground: rnb,
          closeBackgroundHoles: cbh,
          removeSmallIslands: rsi,
          speckleAutoCleanup: sac,
          makeFreestanding: mfs,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        updatePreviews(data);
        setErrorState(false);

        if (Array.isArray(data.color_names) && data.color_names.length > 0) {
          const baseResolved = COLOR_NAMES.find(
            (c) => c.toLowerCase() === data.color_names[0].toLowerCase()
          );
          setBaseColor(baseResolved || data.color_names[0]);
          const userColors = data.color_names
            .slice(1)
            .map(
              (c) =>
                COLOR_NAMES.find(
                  (name) => name.toLowerCase() === c.toLowerCase()
                ) || c
            );
          setColorList(userColors);
        }
      } else {
        console.error('Update Design failed:', await response.text());
        setErrorState(true);
        setIsProcessing(false);
      }
    } catch (err) {
      console.error('Error during update:', err);
      setErrorState(true);
      setIsProcessing(false);
    }
  };

  // ---- Initialize Advanced Color Panel from metadata ----
  useEffect(() => {
    if (!hydrateGate) return;
    if (userIntentRef.current === 'upload') return;
    if (baseColor || colorList.length > 0) return;

    const snapColor = (c) => {
      if (!c) return '';
      const found = COLOR_NAMES.find(
        (n) => n.toLowerCase() === String(c).toLowerCase()
      );
      return found || c;
    };

    const raw = [
      (initialBaseColor || '').trim(),
      ...((initialColorList || []).map((c) => (c || '').trim())),
    ].filter(Boolean);

    if (raw.length >= 2 && (processedImage || overlayImage)) {
      const snappedBase = snapColor(raw[0]);
      const snappedRest = raw.slice(1).map(snapColor);

      setBaseColor(snappedBase);
      setColorList(snappedRest);

      const total = Math.min(8, Math.max(2, 1 + snappedRest.length));
      setNumColors(total);
      setPendingNumColors(total);

      setDropdownDefaultsVisible(false);
      setShowColorControls(true);
      setIsProcessed(true);
      setProcessDisabled(false);
    }
  }, [
    initAdvancedPanelKey,
    processedImage,
    overlayImage,
    initialBaseColor,
    initialColorList,
    baseColor,
    colorList.length,
    hydrateGate,
  ]);

  // hydrate base SVG when overlay present but processed missing
  useEffect(() => {
    if (!isProcessed) return;
    if (!overlayImage || processedImage) return;
    if (!email || !filename) return;

    const username = (email || '').split('@')[0];
    const baseNoExt = String(filename || '')
      .split('.')
      .slice(0, -1)
      .join('');
    if (!username || !baseNoExt) return;

    const root = `/preview/${username}/output-${baseNoExt}`;
    const ts = Date.now();

    (async () => {
      try {
        const res = await fetch(
          `${normalizeUrl(`${root}/metadata.json`)}?ts=${ts}`,
          { cache: 'no-store' }
        );
        if (!res.ok) {
          console.warn(
            '[ImageProcessor] metadata.json missing in overlay-only hydrate:',
            res.status
          );
          return;
        }
        const meta = await res.json();
        let baseRel = String(meta?.base_svg || '').replace(/^\.\//, '').trim();
        if (!baseRel) return;

        if (!baseRel.includes('/')) {
          baseRel = `vectors/base/${baseRel}`;
        }

        setProcessedLoaded(false);
        setProcessedImage(
          `${normalizeUrl(`${root}/${baseRel}`)}?t=${Date.now()}`
        );
      } catch (err) {
        console.error(
          '[ImageProcessor] overlay-only hydrate failed:',
          err
        );
      }
    })();
  }, [isProcessed, overlayImage, processedImage, email, filename, setProcessedImage]);

  // stop spinner when both images ready
  useEffect(() => {
    const bothReady = processedLoaded && (overlayImage ? overlayLoaded : true);
    if (!bothReady) return;

    setIsProcessing(false);

    // Auto-open Advanced Controls after processing finishes (one-shot, stays open)
    if (!autoOpenAdvancedOnceRef.current) {
      autoOpenAdvancedOnceRef.current = true;
      setShowColorControls(true);
      setShowAdvancedOverlayPanel(true);
    }
  }, [processedLoaded, overlayLoaded, overlayImage]);


  // ---- Helper: Advanced Color Controls (shared) ----
  const renderAdvancedControls = () => {
    if (!(processedImage && !dropdownDefaultsVisible && showColorControls)) {
      return null;
    }

    const activeCount = numColors || pendingNumColors;
    const indices = [...Array(activeCount).keys()]; // [0, 1, 2, ...]

    return (
      <>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '1vh',
            fontSize: '1.5vh',
            padding: '0 0.1vw',
            boxSizing: 'border-box',
          }}
        >
          {/* Column headers */}
          <div
            style={{
              display: 'flex',
              width: '100%',
              marginBottom: '1vh',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            <div style={{ flexBasis: '40%', maxWidth: '40%' }}>Color</div>
            <div style={{ flexBasis: '18%', maxWidth: '18%' }}>Inflate</div>
            <div style={{ flexBasis: '20%', maxWidth: '20%' }}>
              Speckle Remove
            </div>
            <div style={{ flexBasis: '22%', maxWidth: '22%' }}>Threshold</div>
          </div>

          {/* Per-row controls */}
          {indices.map((i) => (
            <div
              key={`adv-row-${i}`}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                marginBottom: '0.8vh',
              }}
            >
              {/* COLOR column */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4vh',
                  flexBasis: '40%',
                  maxWidth: '40%',
                  paddingRight: '0.4vh',
                  boxSizing: 'border-box',
                }}
              >
                {/* [x] button */}
                <button
                  disabled={activeCount <= 2 || i === 0}
                  onClick={async () => {
                    if (activeCount <= 2 || i === 0) return;
                    const confirmed = window.confirm(
                      'Are you sure you want to delete this color?'
                    );
                    if (!confirmed) return;

                    const username = (email || '').split('@')[0];
                    const colorName = i === 0 ? baseColor : colorList[i - 1];
                    const pathSafe = (colorName || '')
                      .replace(/[^a-zA-Z0-9\s\-]/g, '')
                      .trim();

                    try {
                      const res = await fetch('/delete-png/', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          username,
                          filename,
                          colorIndex: i,
                          colorName: pathSafe,
                        }),
                      });
                      if (!res.ok) {
                        console.error(
                          'Delete PNG failed:',
                          await res.text()
                        );
                        return;
                      }

                      const newColors = [...colorList];
                      const newInflate = [...inflationValues];
                      const newThresholds = [...thresholdValues];
                      const newSpeckles = [...speckleFlags];

                      if (i > 0) {
                        newColors.splice(i - 1, 1);
                      }
                      newInflate.splice(i, 1);
                      newThresholds.splice(i, 1);
                      newSpeckles.splice(i, 1);

                      const prevCount = activeCount;
                      const newNum = Math.max(2, prevCount - 1);

                      setColorList(newColors);
                      setInflationValues(newInflate);
                      setThresholdValues(newThresholds);
                      setSpeckleFlags(newSpeckles);
                      setPendingNumColors(newNum);
                      setNumColors(newNum);
                      setProcessDisabled(false);

                      const nextInflatePadded = [
                        backgroundInflate,
                        ...Array.from(
                          { length: Math.max(0, newNum - 1) },
                          (_, idx) => Number(newInflate[idx + 1] ?? 0)
                        ),
                      ];
                      const nextSpecklesPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newSpeckles[idx] ?? 0)
                      );
                      const nextThresholdsPadded = Array.from(
                        { length: newNum },
                        (_, idx) => Number(newThresholds[idx] ?? 0)
                      );

                      await handleUpdateDesign({
                        num: newNum,
                        base: baseColor,
                        colors: newColors,
                        background: backgroundInflate,
                        inflate: nextInflatePadded,
                        speckles: nextSpecklesPadded,
                        thresholds: nextThresholdsPadded,
                        rnb: removeNoisyBackground,
                        cbh: closeBackgroundHoles,
                        rsi: removeSmallIslands,
                        sac: speckleAutoCleanup,
                        mfs: makeFreestanding,
                      });
                    } catch (err) {
                      console.error('Error deleting PNG:', err);
                    }
                  }}
                  style={{
                    color: 'red',
                    fontWeight: 'bold',
                    fontSize: '1.5vh',
                    border: 'none',
                    background: 'transparent',
                    cursor:
                      activeCount <= 2 || i === 0 ? 'not-allowed' : 'pointer',
                    flexShrink: 0,
                  }}
                >
                  ✕
                </button>

                {/* Color dropdown */}
                <select
                  className="adv-select"
                  style={{
                    padding: '0.4vh 0.8vh',
                    fontSize: '1.5vh',
                    flex: 1,
                    minWidth: 0,
                    // keep border / radius etc, but *remove* the color here
                    background: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    borderRadius: '0.6vh',
                    backdropFilter: 'blur(4px)',
                  }}
                  value={(() => {
                    const current =
                      (i === 0 ? baseColor : colorList[i - 1]) || '';
                    const match = COLOR_NAMES.find(
                      (n) => n.toLowerCase() === current.toLowerCase()
                    );
                    return match || current;
                  })()}
                  onChange={(e) => {
                    const newVal = e.target.value;

                    if (i === 0) {
                      if (newVal === baseColor) return;
                      const nextBase = newVal;
                      setBaseColor(nextBase);

                      const nextSelected = [
                        (nextBase || '').trim(),
                        ...Array.from(
                          { length: activeCount - 1 },
                          (_, idx) => (colorList[idx] || '').trim()
                        ),
                      ].slice(0, activeCount);
                      scheduleColorUpdate(nextSelected);
                    } else {
                      if (newVal === colorList[i - 1]) return;
                      const updatedColors = [...colorList];
                      updatedColors[i - 1] = newVal;
                      setColorList(updatedColors);

                      const nextSelected = [
                        (baseColor || '').trim(),
                        ...Array.from(
                          { length: activeCount - 1 },
                          (_, idx) => (updatedColors[idx] || '').trim()
                        ),
                      ].slice(0, activeCount);
                      scheduleColorUpdate(nextSelected);
                    }
                  }}
                >
                  {(() => {
                    const current =
                      (i === 0 ? baseColor : colorList[i - 1]) || '';
                    const match = COLOR_NAMES.find(
                      (n) => n.toLowerCase() === current.toLowerCase()
                    );
                    if (!dropdownDefaultsVisible && current && !match) {
                      return <option value={current}>{current}</option>;
                    }
                    return null;
                  })()}

                  {dropdownDefaultsVisible && (
                    <option value="">
                      {i === 0 ? 'Base' : `Color ${i + 1}`}
                    </option>
                  )}

                  {COLOR_NAMES.map((name, idx) => {
                    const isTakenElsewhere =
                      i > 0 &&
                      colorList.some(
                        (val, j) =>
                          j !== i - 1 &&
                          String(val || '')
                            .toLowerCase()
                            .trim() === name.toLowerCase()
                      );
                    return (
                      <option
                        key={idx}
                        value={name}
                        disabled={isTakenElsewhere}
                      >
                        {name}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* INFLATE column */}
              <div
                style={{
                  flexBasis: '18%',
                  maxWidth: '18%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val - 1;

                      if (i === 0) {
                        val = Math.max(10, val); // 10–100 for base
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, val); // 0–10 for others
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* numeric input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={
                      i === 0 ? backgroundInflate : inflationValues[i] || 0
                    }
                    onChange={(e) => {
                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = i === 0 ? '10' : '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      if (i === 0) {
                        val = Math.max(10, Math.min(100, val));
                        setBackgroundInflate(val);
                      } else {
                        val = Math.max(0, Math.min(10, val));
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '4vh',
                      fontSize: '1.5vh',
                      padding: '0.25vh 0.4vh',
                      background: 'rgba(255,255,255,0.12)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: '#e5e7eb',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    onClick={() => {
                      let val =
                        i === 0
                          ? backgroundInflate
                          : inflationValues[i] || 0;
                      val = val + 1;

                      if (i === 0) {
                        val = Math.min(100, val);
                        setBackgroundInflate(val);
                      } else {
                        val = Math.min(10, val);
                        const updated = [...inflationValues];
                        updated[i] = val;
                        setInflationValues(updated);
                      }
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.18)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: 'white',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* SPECKLE column */}
              <div
                style={{
                  flexBasis: '20%',
                  maxWidth: '20%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: i === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <input
                      type="checkbox"
                      disabled={i === 0}
                      checked={!!speckleFlags[i]}
                      onChange={(e) => {
                        if (i === 0) return;
                        const updated = [...speckleFlags];
                        updated[i] = e.target.checked ? 1 : 0;
                        setSpeckleFlags(updated);
                      }}
                      style={{
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        width: '2.2vh',
                        height: '2.2vh',
                        borderRadius: '0.5vh',
                        border: '0.25vh solid rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(15,23,42,0.6)',
                        display: 'inline-block',
                        cursor: i === 0 ? 'not-allowed' : 'pointer',
                      }}
                    />
                    {!!speckleFlags[i] && (
                      <span
                        style={{
                          position: 'absolute',
                          fontSize: '1.7vh',
                          color: 'white',
                          pointerEvents: 'none',
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {/* THRESHOLD column */}
              <div
                style={{
                  flexBasis: '22%',
                  maxWidth: '22%',
                  padding: '0 0.1vh',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.25vh',
                    width: '100%',
                    maxWidth: '100%',
                  }}
                >
                  {/* – button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                    onClick={() => {
                      if (!speckleFlags[i]) return;

                      let val = thresholdValues[i] || 0;
                      val = Math.max(0, val - 1);

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '1.6vh',
                      height: '1.6vh',
                      borderRadius: '50%',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.18)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: speckleFlags[i] ? 'white' : '#9ca3af',
                      fontSize: '1.3vh',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                    }}
                  >
                    –
                  </button>

                  {/* text input */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    disabled={!speckleFlags[i]}
                    value={thresholdValues[i] || 0}
                    onChange={(e) => {
                      if (!speckleFlags[i]) return;

                      let raw = e.target.value.replace(/\D/g, '');
                      if (raw === '') raw = '0';

                      let val = parseInt(raw, 10);
                      if (!Number.isFinite(val)) val = 0;

                      val = Math.max(0, Math.min(200, val));

                      const updated = [...thresholdValues];
                      updated[i] = val;
                      setThresholdValues(updated);
                    }}
                    style={{
                      width: '3.2vh',
                      padding: '0.25vh 0.3vh',
                      fontSize: '1.5vh',
                      background: speckleFlags[i]
                        ? 'rgba(255,255,255,0.12)'
                        : 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      borderRadius: '0.6vh',
                      color: speckleFlags[i] ? '#e5e7eb' : '#9ca3af',
                      textAlign: 'center',
                      outline: 'none',
                      backdropFilter: 'blur(4px)',
                      cursor: speckleFlags[i] ? 'text' : 'not-allowed',
                      flexShrink: 0,
                      boxSizing: 'border-box',
                    }}
                  />

                  {/* + button */}
                  <button
                    type="button"
                    disabled={!speckleFlags[i]}
                  onClick={() => {
                    if (!speckleFlags[i]) return;

                    let val = thresholdValues[i] || 0;
                    val = Math.min(200, val + 1);

                    const updated = [...thresholdValues];
                    updated[i] = val;
                    setThresholdValues(updated);
                  }}
                  style={{
                    width: '1.6vh',
                    height: '1.6vh',
                    borderRadius: '50%',
                    background: speckleFlags[i]
                      ? 'rgba(255,255,255,0.18)'
                      : 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: speckleFlags[i] ? 'white' : '#9ca3af',
                    fontSize: '1.3vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: speckleFlags[i] ? 'pointer' : 'not-allowed',
                    flexShrink: 0,
                  }}
                >
                  +
                </button>
              </div>
            </div>

          </div>
        ))}
      </div>

      {/* Centered Update button */}
      <div
        style={{
          marginTop: '1.5vh',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={() => handleUpdateDesign()}
          style={{
            padding: '0.8vh 2.4vh',
            fontSize: '2vh',
            borderRadius: '8px',
            border: '1px solid #94a3b8',
            fontWeight: 'bold',
            backgroundColor: '#e2e8f0',
            cursor: 'pointer',
          }}
        >
          Update Design
        </button>
      </div>
    </>
  );
};

  // ---- Make 3D – read colors + extrusions and send arrays ----
  const handleMake3DClick = async () => {
    console.log('[SignCustomizationOverlay] Make 3D clicked', {
      isProcessed,
      hasProcessed: !!processedImage,
      hasOverlay: !!overlayImage,
      filename,
    });

    // Show 3D thumbnail box immediately, clear previous STL / thumbnail
    setHasRequested3D(true);
    setIsRendering3D(true);
    setStlUrl(null);
    setThumbnailUrl(null);
    setErrorState(false);
    setProgress(0); // reset progress so 3D generation can go 0 → 100


    // NEW: allow a fresh thumbnail for this render
    hasSavedThumbnailRef.current = false;

    // Safety guard: only try Make 3D if something was actually processed
    if (!isProcessed || (!processedImage && !overlayImage) || !filename) {
      console.warn('[SignCustomizationOverlay] Make 3D guard failed', {
        isProcessed,
        hasProcessed: !!processedImage,
        hasOverlay: !!overlayImage,
        filename,
      });
      setErrorState(true);
      setIsRendering3D(false);
      return;
    }

    // username for filesystem path = composite username (owner:customer)
    const usernameForPath =
      (email || '').split('@')[0] || email || 'guest';
    const baseNoExt = String(filename).split('.').slice(0, -1).join('');

    // Same project root convention as STLGenerator:
    // /outputs/<username>/output-<filenameNoExt>
    const projectRoot = `/outputs/${usernameForPath}/output-${baseNoExt}`;
    setEmsRoot(projectRoot);

    let colorsForUi = [];
    let cleanExtrusions = [];

    // 1) Try reading colors + extrusions from metadata.json
    try {
      const metaUrl = `${projectRoot}/metadata.json?t=${Date.now()}`;

      console.log(
        '[SignCustomizationOverlay] Fetching metadata.json:',
        metaUrl
      );

      const metaRes = await fetch(metaUrl, { cache: 'no-store' });
      if (!metaRes.ok) {
        throw new Error(
          `metadata.json fetch failed: ${metaRes.status} ${metaRes.statusText}`
        );
      }

      const meta = await metaRes.json();
      console.log('[SignCustomizationOverlay] metadata.json loaded:', meta);

      if (Array.isArray(meta.colors) && meta.colors.length > 0) {
        // Sort by index to keep order stable
        const sorted = [...meta.colors].sort((a, b) => {
          const ia = typeof a.index === 'number' ? a.index : 0;
          const ib = typeof b.index === 'number' ? b.index : 0;
          return ia - ib;
        });

        // Helper – extract color name from file path like "1 - Black.svg"
        const pickColorName = (p) => {
          if (!p || typeof p !== 'string') return '';
          const mm = p.match(
            /(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png|stl)$/i
          );
          return mm && mm[1] ? mm[1].trim() : '';
        };

        // Prefer already-hydrated effectiveColors if it lines up with metadata
        if (Array.isArray(effectiveColors) && effectiveColors.length === sorted.length) {
          colorsForUi = [...effectiveColors];
        } else {
          colorsForUi = sorted.map((entry, idx) => {
            if (!entry) return `Color ${idx + 1}`;

            const file =
              entry.file ||
              entry.svg ||
              entry.png ||
              entry.path ||
              entry.stl ||
              '';

            const fromFile = pickColorName(file);
            if (fromFile) return fromFile;

            const name = entry.name || `Color ${entry.index ?? idx + 1}`;
            return String(name).trim();
          });
        }

        // If extrusion missing/invalid, default to 2 for this product type
        cleanExtrusions = sorted.map((entry) => {
          if (!entry) return 2;
          const exRaw = entry.extrusion;
          let ex = Number(exRaw);
          if (!Number.isFinite(ex) || ex <= 0) ex = 2;
          return ex;
        });
      }

    } catch (err) {
      console.error(
        '[SignCustomizationOverlay] Failed to read metadata.json for extrusions:',
        err
      );
      // We will fall back to UI state below
    }

    // 2) Fallback: if metadata didn't give us colors, use UI colors
    if (!colorsForUi.length) {
      const uiColors = [
        (baseColor || '').trim(),
        ...colorList.map((c) => (c || '').trim()),
      ].filter(Boolean);

      colorsForUi = uiColors;
      console.log('[SignCustomizationOverlay] Using UI colors as fallback:', colorsForUi);
    }

    // 3) Fallback: if no extrusions or all invalid, default to 2 for each color
    if (!cleanExtrusions.length && colorsForUi.length) {
      cleanExtrusions = colorsForUi.map(() => 2);
      console.log(
        '[SignCustomizationOverlay] Using default extrusions (2) for all colors:',
        cleanExtrusions
      );
    } else if (cleanExtrusions.length && colorsForUi.length) {
      // Sanitize whatever we got
      cleanExtrusions = cleanExtrusions.map((ex) => {
        let v = Number(ex);
        if (!Number.isFinite(v) || v <= 0) v = 2;
        return v;
      });
    }

    // 4) If we still have no colors at all, we truly can't continue
    if (!colorsForUi.length) {
      console.error(
        '[SignCustomizationOverlay] No colors available for /generate-stl/. Aborting.'
      );
      setErrorState(true);
      setIsRendering3D(false);
      return;
    }

    // Make sure lengths match
    if (cleanExtrusions.length < colorsForUi.length) {
      const last = cleanExtrusions[cleanExtrusions.length - 1] ?? 2;
      cleanExtrusions = [
        ...cleanExtrusions,
        ...Array(colorsForUi.length - cleanExtrusions.length).fill(last),
      ];
    } else if (cleanExtrusions.length > colorsForUi.length) {
      cleanExtrusions = cleanExtrusions.slice(0, colorsForUi.length);
    }

    console.log(
      '[SignCustomizationOverlay] Final colors/extrusions for /generate-stl/:',
      { colorsForUi, cleanExtrusions }
    );
    setEffectiveColors(colorsForUi);

    // 5) Build payload (same shape as STLGenerator.js)
    const payload = {
      filename,
      email,                // composite owner:customer
      colors: colorsForUi,  // ["Base", "Grey", "Light Blue"]
      extrusions: cleanExtrusions, // [2, 2, 2] (or from metadata)
      makeItFlat: false,
    };

    console.log(
      '[SignCustomizationOverlay] Calling /generate-stl/ with payload:',
      payload
    );

    try {
      const res = await fetch('/generate-stl/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error(
          '[SignCustomizationOverlay] Sign 3D generation failed:',
          txt
        );
        setErrorState(true);
        return;
      }

      const data = await res.json().catch(() => ({}));
      console.log('[SignCustomizationOverlay] /generate-stl/ response:', data);

      // Ignore any "stl" field from backend and load from the same directory
      // convention as STLGenerator:
      //   /outputs/<username>/output-<filenameNoExt>/stls/combined/combined_model.stl
      const ts = Date.now();
      const combinedPath = `${projectRoot}/stls/combined/combined_model.stl`;
      const previewUrl = normalizeUrl(`${combinedPath}?t=${ts}`);

      console.log(
        '[SignCustomizationOverlay] Using combined STL URL for preview:',
        previewUrl
      );
      setStlUrl(previewUrl);

    } catch (err) {
      console.error(
        '[SignCustomizationOverlay] Error during sign 3D generation:',
        err
      );
      setErrorState(true);
    } finally {
      setIsRendering3D(false);
    }
  };

const handleMainButtonClick = () => {
  console.log('[SignCustomizationOverlay] Main button clicked', {
    credits,
    processDisabled,
    filename,
    isProcessed,
    hasProcessed: !!processedImage,
    hasOverlay: !!overlayImage,
    hasRequested3D,
    stlUrl,
  });

  // Respect disabled state
  if (credits === 0 || processDisabled || !filename) return;

  // If 3D is already generated → open "Send to Maker" overlay
  if (hasRequested3D && stlUrl && !isRendering3D) {
    setShowSendOverlay(true);
    return;
  }

  // If we already have a processed result → "Make 3D" behavior
  if (isProcessed && (processedImage || overlayImage)) {
    handleMake3DClick();
  } else {
    // Normal processing behavior
    handleProcessClick(false);
  }
};

const handleSendOrderSubmit = async () => {
  console.log('[SendToMaker] SUBMIT handler called', {
    sendNotes,
    filename,
    email,
    ownerUsername,
    customerEmail,
  });

  if (!filename || !ownerUsername || !customerEmail) {
    console.warn('[SendToMaker] Missing required fields.', {
      filename,
      ownerUsername,
      customerEmail,
    });
    // Close "Send" overlay and show confirmation anyway
    setShowSendOverlay(false);
    setShowConfirmationOverlay(true);
    return;
  }

  // projectSlug = filename without extension (matches output-<slug> folder)
  const baseNoExt = String(filename).split('.').slice(0, -1).join('');
  const projectSlug = baseNoExt || filename;

  // customerName = local part of email (before @)
  const customerName =
    (customerEmail && customerEmail.includes('@'))
      ? customerEmail.split('@')[0]
      : (customerEmail || 'customer');

  const payload = {
    ownerUsername: ownerUsername,         
    customerName,                         
    projectSlug,                         
    customerEmail: customerEmail,         // FULL email
    notes: sendNotes || '',
  };

  try {
    const res = await fetch('/save-customer-order/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    console.log('[SendToMaker] Response status:', res.status);
  } catch (err) {
    console.error('[SendToMaker] Error saving customer_order.json:', err);
    // still continue to confirmation overlay — UX choice
  }

  setShowSendOverlay(false);
  setShowConfirmationOverlay(true);
};

const RenderLockLayer = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      zIndex: 2147483647,          // max-ish z-index
      backgroundColor: 'rgba(255,255,255,0.65)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'wait',
      pointerEvents: 'all',        // MUST capture events
      touchAction: 'none',
      userSelect: 'none',
    }}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
    onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
  >
    <img
      src="/rendering.gif"
      alt="Rendering"
      style={{ width: 200, height: 200, objectFit: 'contain' }}
    />
  </div>
);


  // ─────────── RENDER ───────────
  return (
    
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',      // center horizontally
      justifyContent: 'flex-start', // ⬅ top-align vertically
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      paddingTop: 'vh',         // ⬅ tweak this to control clearance (or set to '0')
    }}
  >
        <input
          type="file"
          accept="image/jpeg,image/png"
          ref={fileInputRef}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
        />

        {/* FLEX ROW: Orange (images) + Yellow (controls) */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'flex-start',
            gap: '0vw',
            height: 'auto',
          }}
        >
          {/* ORANGE BOX */}
          <div
            ref={orangeBoxRef}
            style={{
              border: '0px solid orange',
              padding: '0.5vw',  // or 0
              width: '81vmin',
              height: '81vmin',
              maxWidth: '100%',
              maxHeight: '100%',
              overflowY: 'auto',
              boxSizing: 'border-box',
              display: 'block',
              margin: '0 auto',
            }}
          >

            {/* Input Image */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',      // ⬅ fills the orange container
                marginBottom: '2vh',
              }}
            >
              <div
                  style={{
                    ...boxStyle(),      // ⬅ uses the 96% square
                    position: 'relative',
                    cursor: credits > 0 && !isProcessing ? 'pointer' : 'not-allowed',

                    opacity: credits > 0 ? 1 : 0.6,
                  }}
                onClick={credits > 0 && !isProcessing ? handleInputClick : undefined}
              >
              {isProcessing && <RenderLockLayer />}
                {/* Sliding BASIC controls overlay on INPUT IMAGE BOX */}
                {inputImage && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      zIndex: 30,
                      background:
                        'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
                      color: 'white',
                      transform: showInputOverlayPanel
                        ? 'translateY(0%)'
                        : 'translateY(calc(-100% + 2.8vh))',
                      transition: 'transform 0.25s ease-in-out',
                      willChange: 'transform',
                      fontSize: '0.8rem',
                      pointerEvents: 'auto',
                    }}
                  >
                    {/* Handle */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowInputOverlayPanel((prev) => !prev);
                      }}
                      style={{
                        height: '2.8vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.4)',
                        backgroundColor: 'rgba(112,112,112,0.4)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '2.5vh',
                          fontWeight: 600,
                          letterSpacing: '0.03em',
                        }}
                      >
                        {showInputOverlayPanel
                          ? '▲ Basic Controls ▲'
                          : '▼ Basic Controls ▼'}
                      </span>
                    </div>

                    {/* CONTENT: Basic Controls (animated open/close) */}
                    <div
                      style={{
                        padding: showInputOverlayPanel
                          ? '8px 10px 10px'
                          : '0 10px 0',
                        maxHeight: showInputOverlayPanel ? '55%' : 0,
                        overflowY: 'auto',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(112,112,112,0.5)',
                        fontSize: '2vh',
                        transition:
                          'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                        opacity: showInputOverlayPanel ? 1 : 0,
                      }}
                    >
                      {/* Number of Colors */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          marginBottom: '8px',
                          gap: '1vh',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '2.5vh',
                            fontWeight: 600,
                            color: '#e5e7eb',
                            letterSpacing: '0.5px',
                            marginBottom: '0.5vh',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Number of Colors:
                        </span>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1vh',
                          }}
                        >
                          {/* – button */}
                          <button
                            type="button"
                            onClick={() => {
                              let value = Math.max(2, pendingNumColors - 1);
                              setPendingNumColors(value);
                              setProcessDisabled(false);
                              setDropdownDefaultsVisible(true);
                              setIsProcessed(false);
                              setProcessedImage(null);
                              setOverlayImage(null);
                              setIsProcessing(false);
                              setProcessedLoaded(false);
                              setOverlayLoaded(false);
                            }}
                            style={{
                              width: '3vh',
                              height: '3vh',
                              borderRadius: '50%',
                              background: 'rgba(255,255,255,0.18)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              color: 'white',
                              fontSize: '2vh',
                              lineHeight: 0,
                              textAlign: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            –
                          </button>

                          {/* numeric input */}
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={String(pendingNumColors)}
                            onChange={(e) => {
                              // keep only digits
                              let raw = e.target.value.replace(/\D/g, '');
                              if (raw === '') {
                                // if user clears, don’t crash – default to 2
                                raw = '2';
                              }
                              let value = parseInt(raw, 10);
                              if (!Number.isFinite(value)) value = 2;

                              // clamp between 2 and 8
                              value = Math.max(2, Math.min(8, value));

                              setPendingNumColors(value);
                              setProcessDisabled(false);
                              setDropdownDefaultsVisible(true);
                              setIsProcessed(false);
                              setProcessedImage(null);
                              setOverlayImage(null);
                              setIsProcessing(false);
                              setProcessedLoaded(false);
                              setOverlayLoaded(false);
                            }}
                            style={{
                              width: '3vh',
                              fontSize: '2vh',
                              padding: '0.4vh 0.6vh',
                              background: 'rgba(255,255,255,0.12)',
                              border: '1px solid rgba(255,255,255,0.25)',
                              borderRadius: '0.6vh',
                              color: 'white',
                              textAlign: 'center',
                              outline: 'none',
                              backdropFilter: 'blur(4px)',
                              margin: 0,
                            }}
                          />

                          {/* + button */}
                          <button
                            type="button"
                            onClick={() => {
                              let value = Math.min(8, pendingNumColors + 1);
                              setPendingNumColors(value);
                              setProcessDisabled(false);
                              setDropdownDefaultsVisible(true);
                              setIsProcessed(false);
                              setProcessedImage(null);
                              setOverlayImage(null);
                              setIsProcessing(false);
                              setProcessedLoaded(false);
                              setOverlayLoaded(false);
                            }}
                            style={{
                              width: '3vh',
                              height: '3vh',
                              borderRadius: '50%',
                              background: 'rgba(255,255,255,0.18)',
                              border: '1px solid rgba(255,255,255,0.3)',
                              color: 'white',
                              fontSize: '2vh',
                              lineHeight: 0,
                              textAlign: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Flags (compact) */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.4vh',
                          fontSize: '2vh',
                        }}
                      >
                        {/* Remove Noisy Background */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            gap: '1vh',
                          }}
                        >
                          <span>Remove Noisy Background</span>
                          <span
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={removeNoisyBackground}
                              onChange={(e) => {
                                setRemoveNoisyBackground(e.target.checked);
                                setDropdownDefaultsVisible(true);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setProcessDisabled(false);
                                setIsProcessed(false);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }}
                              style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                width: '2.4vh',
                                height: '2.4vh',
                                borderRadius: '0.5vh',
                                border: '0.25vh solid rgba(255,255,255,0.8)',
                                backgroundColor: 'rgba(15,23,42,0.6)',
                                display: 'inline-block',
                                cursor: 'pointer',
                              }}
                            />
                            {removeNoisyBackground && (
                              <span
                                style={{
                                  position: 'absolute',
                                  fontSize: '1.8vh',
                                  color: 'white',
                                  pointerEvents: 'none',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </span>
                        </label>

                        {/* Close Background Holes */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            gap: '1vh',
                          }}
                        >
                          <span>Close Background Holes</span>
                          <span
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={closeBackgroundHoles}
                              onChange={(e) => {
                                setCloseBackgroundHoles(e.target.checked);
                                setDropdownDefaultsVisible(true);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setProcessDisabled(false);
                                setIsProcessed(false);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }}
                              style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                width: '2.4vh',
                                height: '2.4vh',
                                borderRadius: '0.5vh',
                                border: '0.25vh solid rgba(255,255,255,0.8)',
                                backgroundColor: 'rgba(15,23,42,0.6)',
                                display: 'inline-block',
                                cursor: 'pointer',
                              }}
                            />
                            {closeBackgroundHoles && (
                              <span
                                style={{
                                  position: 'absolute',
                                  fontSize: '1.8vh',
                                  color: 'white',
                                  pointerEvents: 'none',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </span>
                        </label>

                        {/* Remove Small Islands */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            gap: '1vh',
                          }}
                        >
                          <span>Remove Small Islands</span>
                          <span
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={removeSmallIslands}
                              onChange={(e) => {
                                setRemoveSmallIslands(e.target.checked);
                                setDropdownDefaultsVisible(true);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setProcessDisabled(false);
                                setIsProcessed(false);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }}
                              style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                width: '2.4vh',
                                height: '2.4vh',
                                borderRadius: '0.5vh',
                                border: '0.25vh solid rgba(255,255,255,0.8)',
                                backgroundColor: 'rgba(15,23,42,0.6)',
                                display: 'inline-block',
                                cursor: 'pointer',
                              }}
                            />
                            {removeSmallIslands && (
                              <span
                                style={{
                                  position: 'absolute',
                                  fontSize: '1.8vh',
                                  color: 'white',
                                  pointerEvents: 'none',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </span>
                        </label>

                        {/* Speckle Auto-Cleanup */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            gap: '1vh',
                          }}
                        >
                          <span>Speckle Auto-Cleanup</span>
                          <span
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={speckleAutoCleanup}
                              onChange={(e) => {
                                setSpeckleAutoCleanup(e.target.checked);
                                setDropdownDefaultsVisible(true);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setProcessDisabled(false);
                                setIsProcessed(false);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }}
                              style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                width: '2.4vh',
                                height: '2.4vh',
                                borderRadius: '0.5vh',
                                border: '0.25vh solid rgba(255,255,255,0.8)',
                                backgroundColor: 'rgba(15,23,42,0.6)',
                                display: 'inline-block',
                                cursor: 'pointer',
                              }}
                            />
                            {speckleAutoCleanup && (
                              <span
                                style={{
                                  position: 'absolute',
                                  fontSize: '1.8vh',
                                  color: 'white',
                                  pointerEvents: 'none',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </span>
                        </label>

                        {/* Make Freestanding */}
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            gap: '1vh',
                          }}
                        >
                          <span>Make Freestanding</span>
                          <span
                            style={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={makeFreestanding}
                              onChange={(e) => {
                                setMakeFreestanding(e.target.checked);
                                setDropdownDefaultsVisible(true);
                                setProcessedImage(null);
                                setOverlayImage(null);
                                setProcessDisabled(false);
                                setIsProcessed(false);
                                setIsProcessing(false);
                                setProcessedLoaded(false);
                                setOverlayLoaded(false);
                              }}
                              style={{
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                width: '2.4vh',
                                height: '2.4vh',
                                borderRadius: '0.5vh',
                                border: '0.25vh solid rgba(255,255,255,0.8)',
                                backgroundColor: 'rgba(15,23,42,0.6)',
                                display: 'inline-block',
                                cursor: 'pointer',
                              }}
                            />
                            {makeFreestanding && (
                              <span
                                style={{
                                  position: 'absolute',
                                  fontSize: '1.8vh',
                                  color: 'white',
                                  pointerEvents: 'none',
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}


                {inputImage ? (
                  <>
                    <img
                      src={inputImage}
                      alt="Input"
                      style={imgStyle}
                      onLoad={() => {
                        orangeBoxRef.current?.scrollTo({
                          top: 0,
                          behavior: 'smooth',
                        });
                      }}
                    />

                  </>
                ) : (
                  <span
                    style={{
                      fontSize: '2.8vh',
                      color: '#64748b',
                      textAlign: 'center',
                      padding: '1vw',
                    }}
                  >
                    Click Here to upload image
                  </span>
                )}
              </div>
            </div>
            {/* Processed + Overlay */}
            {(processedImage || overlayImage) && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <div
                  style={{
                    ...boxStyle(),      // 96% square inside the orange container
                    position: 'relative',
                  }}
                >
                  {isProcessing && <RenderLockLayer />}
                  {/* Sliding Advanced Controls overlay on PROCESSED IMAGE BOX */}
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      zIndex: 30,
                      background:
                        'linear-gradient(to bottom, rgba(15,23,42,0.96), rgba(15,23,42,0.6), transparent)',
                      color: 'white',
                      transform: showAdvancedOverlayPanel
                        ? 'translateY(0%)'
                        : 'translateY(calc(-100% + 2.8vh))',
                      transition: 'transform 0.25s ease-in-out',
                      willChange: 'transform',
                      fontSize: '0.8rem',
                      pointerEvents: 'auto',
                    }}
                  >

                    {/* Handle */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAdvancedOverlayPanel((prev) => {
                          const next = !prev;
                          if (next) capitanApi.onOpenAdvanced();
                          return next;
                        });
                        if (!showColorControls) {
                          setShowColorControls(true);
                        }
                      }}
                      style={{
                        height: '2.8vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(255,255,255,0.4)',
                        backgroundColor: 'rgba(112,112,112,0.4)',
                      }}
                    >
                      <span
                        style={{
                          fontSize: '2.5vh',
                          fontWeight: 600,
                          letterSpacing: '0.03em',
                        }}
                      >
                        {showAdvancedOverlayPanel
                          ? '▲ Advanced Color Controls ▲'
                          : '▼ Advanced Color Controls ▼'}
                      </span>
                    </div>

                    {/* CONTENT: Advanced Color Controls (animated open/close) */}
                    <div
                      style={{
                        padding: showAdvancedOverlayPanel
                          ? '8px 10px 10px'
                          : '0 10px 0',
                        maxHeight: showAdvancedOverlayPanel ? '55%' : 0,
                        overflowY: 'auto',
                        overflow: 'hidden',
                        backgroundColor: 'rgba(112,112,112,0.5)',
                        fontSize: '2vh',
                        opacity: showAdvancedOverlayPanel ? 1 : 0,
                        transition:
                          'max-height 0.25s ease, opacity 0.25s ease, padding 0.25s ease',
                      }}
                    >
                      {/* Injected custom styling for advanced controls */}
                      <style>
                        {`
                          /* dropdown element itself */
                          .adv-select {
                            background: rgba(255,255,255,0.12) !important;
                            border: 1px solid rgba(255,255,255,0.25) !important;
                            border: 1px solid rgba(148,163,184,0.9) !important;
                            border-radius: 0.6vh;
                            backdrop-filter: blur(4px);
                          }

                          /* options inside the dropdown */
                          .adv-select option {
                            background-color: #ffffff;
                            color: #000000;
                          }

                          /* selected option: slightly grey text, same white background */
                          .adv-select option:checked,
                          .adv-select option[selected] {
                            color: #6b7280; /* tailwind's gray-500 */
                            background-color: #ffffff;
                          }

                          /* textboxes */
                          .adv-input {
                            background-color: #ffffff !important;
                            border: 1px solid rgba(148,163,184,0.9) !important;
                            color: #000000 !important;
                            backdrop-filter: blur(4px);
                          }

                          /* threshold disabled state */
                          .adv-input-disabled {
                            background-color: #f3f4f6 !important;
                            color: #9ca3af !important;
                          }
                        `}
                      </style>


                      {renderAdvancedControls({
                        selectClass: 'adv-select',
                        inputClass: 'adv-input',
                        inputDisabledClass: 'adv-input-disabled',
                        buttonWrapperStyle: {
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '1vh',
                        },
                      })}
                    </div>


                  </div>


                  {processedImage && (
                    <img
                      src={processedImage}
                      alt="Processed"
                      style={layerStyle(1)}
                      onLoad={() => {
                        setProcessedLoaded(true);
                        setTimeout(() => {
                          orangeBoxRef.current?.scrollTo({
                            top: orangeBoxRef.current.scrollHeight,
                            behavior: 'smooth',
                          });
                        }, 100);
                      }}
                    />
                  )}
                  {overlayImage && (
                    <img
                      src={overlayImage}
                      alt="Overlay"
                      style={layerStyle(2)}
                      onLoad={() => {
                        setOverlayLoaded(true);
                        setTimeout(() => {
                          orangeBoxRef.current?.scrollTo({
                            top: orangeBoxRef.current.scrollHeight,
                            behavior: 'smooth',
                          });
                        }, 100);
                      }}
                    />
                  )}
                </div>
              </div>
            )}

          {/* 3D Thumbnail box (Sign render snapshot only – no live viewer) */}
          {hasRequested3D && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                marginTop: '2vh',
              }}
            >
              <div
                style={{
                  ...boxStyle(),
                  position: 'relative',
                  borderStyle: 'dashed',
                }}
              >
                {thumbnailUrl ? (
                  <img
                    src={thumbnailUrl}
                    alt="3D Render"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                    }}
                  />
                ) : (
                  <span
                    style={{
                      fontSize: '2.2vh',
                      color: '#64748b',
                      textAlign: 'center',
                      padding: '1vw',
                    }}
                  >
                    3D preview will appear here once it is ready…
                  </span>
                )}

                {/* Off-screen WebGL renderer (kept invisible; used only for thumbnail capture) */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    ref={threeContainerRef}
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          </div>
        </div>

      {/* Unified Process / Progress / Send button */}
      <div
        style={{
          marginTop: '0vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={handleMainButtonClick}
          disabled={
            credits === 0 ||
            processDisabled ||
            !filename // ⬅ require filename (upload finished)
          }
          style={{
            width: '80vmin',
            padding: '0.8vmin 0',
            background: errorState
              ? '#fecaca'
              : isProcessing
              ? '#93c5fd'
              : isRendering3D
              ? '#bbf7d0' // soft amber while generating 3D
              : canSendToMaker
              ? '#fbbf24' // amber when ready to send
              : isProcessed && (processedImage || overlayImage)
              ? '#bbf7d0' // light green for "Make 3D"
              : '#93c5fd',
            border: '0px solid #94a3b8',
            borderRadius: '10px',
            fontSize: '2.8vh',
            fontWeight: 'bold',
            textAlign: 'center',
            cursor:
              credits > 0 &&
              !processDisabled &&
              filename
                ? 'pointer'
                : 'not-allowed',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Progress bar: image processing & 3D generation both use real % */}
          {progress !== null && (isProcessing || isRendering3D) && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${progress}%`,
                backgroundColor: errorState ? '#f87171' : '#0ea5e9',
                transition: 'width 0.4s ease',
                zIndex: 0,
              }}
            ></div>
          )}

          <span
            style={{
              position: 'relative',
              zIndex: 2,
              animation:
                !errorState &&
                !isProcessing &&
                !isRendering3D &&
                !canSendToMaker &&
                isProcessed &&
                (processedImage || overlayImage)
                  ? 'flashColor 1.2s infinite linear'
                  : 'none',
            }}
          >
            {errorState
              ? 'ERROR'
              : isProcessing
              ? `Processing… ${progress ?? 0}%`
              : isRendering3D
              ? `Generating 3D… ${progress ?? 0}%`
              : canSendToMaker
              ? 'Send to Maker'
              : isProcessed && (processedImage || overlayImage)
              ? 'Make 3D'
              : 'Process Image'}
          </span>
        </button>
      </div>

      {/* 4) ⬇️ NEW: "Send to Maker" overlay */}
        {showSendOverlay && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.55)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              padding: '4vmin', // ensures content never touches screen edges
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '80vh',
                padding: '3vmin 3vmin', // ⬅ equal left/right padding
                boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',     // ensures internal scroll instead of overflow
              }}
            >
              {/* Header text */}
              <div
                style={{
                  fontSize: '2.4vh',
                  fontWeight: 700,
                  marginBottom: '1.2vh',
                }}
              >
                Send this design to the maker
              </div>

              {/* Sub-text */}
              <div
                style={{
                  fontSize: '1.8vh',
                  color: '#4b5563',
                  marginBottom: '1.8vh',
                }}
              >
                Add any notes or instructions for the store owner.
              </div>

              {/* Scroll wrapper for large content */}
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  paddingRight: '0.5vmin', // avoids scrollbar overlap on text
                }}
              >
                <textarea
                  value={sendNotes}
                  onChange={(e) => setSendNotes(e.target.value)}
                  placeholder="Write your notes here…"
                  style={{
                    width: '100%',
                    minHeight: '30vh',
                    resize: 'vertical',
                    padding: '1.2vh 1vw',
                    fontSize: '1.8vh',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Buttons row */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '1vw',
                  marginTop: '2vh',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowSendOverlay(false)}
                  style={{
                    padding: '0.8vh 2vw',
                    fontSize: '1.8vh',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#f9fafb',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleSendOrderSubmit}
                  style={{
                    padding: '0.8vh 2.4vw',
                    fontSize: '1.8vh',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: '#22c55e',
                    color: 'white',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Submit
                </button>

              </div>
            </div>
          </div>
        )}
      {/* Small bottom confirmation overlay + tinted background */}
      {showConfirmationOverlay && (
        <>
          {/* Dimmed backdrop */}
          <div
            onClick={() => {
              setShowConfirmationOverlay(false);
              onClose?.();
            }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(2px)',
              zIndex: 100000,
            }}
          />

          {/* Floating confirmation box */}
          <div
            style={{
              position: 'fixed',
              left: '50%',
              bottom: '3vh',
              transform: 'translateX(-50%)',
              zIndex: 100001,
              maxWidth: '420px',
              width: '90vw',
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
              padding: '1.8vh 2vw',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              boxSizing: 'border-box',
            }}
          >
            <div
              style={{
                fontSize: '1.9vh',
                fontWeight: 600,
                marginBottom: '1vh',
                textAlign: 'center',
              }}
            >
              Your request has been sent to The Maker.
            </div>

            <div
              style={{
                fontSize: '1.7vh',
                color: '#4b5563',
                marginBottom: '1.6vh',
                textAlign: 'center',
              }}
            >
              The Maker will respond to you via Email.
            </div>

            <button
              type="button"
              onClick={() => {
                setShowConfirmationOverlay(false);
                onClose?.();
              }}
              style={{
                alignSelf: 'center',
                padding: '0.7vh 2.4vw',
                fontSize: '1.8vh',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: '#22c55e',
                color: 'white',
                fontWeight: 600,
                cursor: 'pointer',
                minWidth: '90px',
              }}
            >
              OK
            </button>
          </div>
        </>
      )}

{/* Capitan overlay (bottom-left). Button is STACKED BELOW and anchored bottom-left. */}
{showCapitan && !capitanDisabled && (
  <div id="capitan-nudge" style={CAPITAN_STYLES.capitanWrap}>
    <div style={CAPITAN_STYLES.capitanBox}>
      {/* close (X) — dismiss until action SVG changes */}
      <button
        onClick={dismissCapitan}
        aria-label="Close capitan"
        style={CAPITAN_STYLES.capitanClose}
      >
        ×
      </button>

      {/* image + action svg */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
        {/* ✅ Animated Capitan SVG (must NOT be <img>) */}
        <object
          key={capitanImgSrc}                 // ✅ forces remount/reload when src changes
          data={capitanImgSrc}
          type="image/svg+xml"
          aria-label="Capitan"
          style={{
            display: 'block',
            border: 'none',
            background: 'transparent',
            overflow: 'hidden',
            ...CAPITAN_STYLES.capitanImg,             // keeps your sizing
          }}
        />

        {/* ✅ Animated action SVG (must NOT be <img>) */}
        <object
          key={capitanActionSvgSrc}           // ✅ forces remount/reload when src changes
          data={capitanActionSvgSrc}
          type="image/svg+xml"
          aria-label="Capitan Action"
          style={{
            display: 'block',
            height: capitanSvgHeight,
            width: 'auto',
            border: 'none',
            background: 'transparent',
            overflow: 'hidden',
          }}
        />
      </div>
    </div>

    {/* Don't show again — anchored bottom-left BELOW image */}
    <button onClick={disableCapitan} style={CAPITAN_STYLES.nudgeBtn}>
      Don’t show again
    </button>
  </div>
)}


    </div>
  );
}


const boxStyle = () => ({
  width: '100%',
  height: '100%',
  border: '2px dashed #94a3b8',
  borderRadius: '10px',
  backgroundColor: '#f8fafc',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
});


const imgStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const labelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const layerStyle = (z) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  top: 0,
  left: 0,
  zIndex: z,
  pointerEvents: 'none',
});